// MediaFrame.tsx — a single image in the feed. THIS IS THE RUNAWAY-HEIGHT FIX.
//
// The frame's WIDTH is derived from the height budget (`--media-cap * --ar`), so a tall photo
// meets the budget by narrowing rather than by being cropped. The letterbox pillars are simply
// the card's own surface showing through, so the frame never paints its own colour and stays
// correct whatever the user picks in Card Appearance.

import { useState, type CSSProperties } from 'react';
import styled from 'styled-components';
import type { PostImage } from '@/core/models/post';
import { aspectOf } from '../postImages';
import { mediaCap, mediaCapHero } from './mediaCap';
import { AR_RESERVE, brackets, resolveFit, type FitMode } from './frameKit';

/** Natural ratios measured from legacy posts that carry no stored dimensions.
 *  Module-level so a given URL is measured at most once per session. */
const ratioMemo = new Map<string, number>();
const MEMO_CAP = 200;

function rememberRatio(url: string, ratio: number): void {
  if (ratioMemo.size >= MEMO_CAP) {
    const oldest = ratioMemo.keys().next().value;
    if (oldest !== undefined) ratioMemo.delete(oldest);
  }
  ratioMemo.set(url, ratio);
}

/* ── Styled ──────────────────────────────────────────────────────────────────── */

// OUTER — bracket anchor + focus ring. MUST NOT set `overflow`, or the -1px bracket
// offsets get clipped and the affordance silently vanishes. Clipping happens on Clip below.
const Frame = styled.button<{ $hero: boolean }>`
  ${({ $hero }) => ($hero ? mediaCapHero : mediaCap)}
  appearance: none;
  padding: 0;
  border: 0;
  margin: 0;
  font: inherit;
  display: block;
  position: relative;
  line-height: 0;
  background: transparent;
  cursor: zoom-in;
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;

  /* ── THE FIX ────────────────────────────────────────────────────────────────
     Width follows the height budget, so tall images narrow instead of cropping.
     max-height is a belt-and-braces guard if the viewport shrinks after layout. */
  width: min(100%, calc(var(--media-cap) * var(--ar)));
  aspect-ratio: var(--ar);
  max-height: var(--media-cap);

  /* MANDATORY. PostCard/HeroPostCard's Article is display:flex; flex-direction:column, and
     the default align-items:stretch would blow this back to full column width, silently
     defeating the whole design while looking like it shipped. center (rather than
     flex-start) also keeps a narrowed portrait centred in the card, matching MediaDeck. */
  align-self: center;

  ${brackets}

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.focusRing};
    outline-offset: 3px;
  }
`;

// INNER — the clipping layer. radius.sm, NOT radius.md: applyPreferences scales
// sm = radiusPx*0.5 and md = radiusPx*0.75, while Panel uses radius.sm — so media at md was
// rounder than the card containing it at every slider position.
const Clip = styled.div<{ $fit: FitMode; $pos: string }>`
  width: 100%;
  height: 100%;
  overflow: hidden;
  border-radius: ${({ theme }) => theme.radius.sm};
  contain: paint;
  /* transparent: the pillars/bars ARE the user's card colour + material. */
  background: transparent;

  & > img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: ${({ $fit }) => $fit};
    object-position: ${({ $pos }) => $pos};
  }
`;

// FILL MODE (profile / community Media tabs). Bypasses the cap entirely and renders a plain
// div — NEVER a button: those tiles are already inside a styled(Link) with
// `> * { pointer-events: none }`, so a nested button is both invalid HTML and inert.
const FillFrame = styled.div`
  width: 100%;
  height: 100%;
  overflow: hidden;
  border-radius: ${({ theme }) => theme.radius.sm};
  & > img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

/* ── Component ───────────────────────────────────────────────────────────────── */

export interface MediaFrameProps {
  item: PostImage;
  /** Square-tile mode for the profile/community Media grids. */
  fill?: boolean;
  /** Post detail gets a larger height budget than the feed. */
  variant?: 'feed' | 'hero';
  /** Eager-load the first image in the feed; lazy-load the rest. */
  priority?: boolean;
  onOpen?: () => void;
}

export function MediaFrame({
  item,
  fill = false,
  variant = 'feed',
  priority = false,
  onOpen,
}: MediaFrameProps) {
  const stored = aspectOf(item);
  const [measured, setMeasured] = useState<number | null>(
    () => stored ?? ratioMemo.get(item.url) ?? null,
  );

  const imgProps = {
    src: item.url,
    alt: item.alt || '',
    loading: priority ? ('eager' as const) : ('lazy' as const),
    decoding: 'async' as const,
  };

  if (fill) {
    return (
      <FillFrame data-testid="post-media-image">
        <img {...imgProps} />
      </FillFrame>
    );
  }

  // Unknown dimensions (legacy post): reserve a sane box, then correct once on load.
  const ratio = measured ?? AR_RESERVE;
  const { ar, fit, objectPosition } = resolveFit(ratio);

  return (
    <Frame
      type="button"
      $hero={variant === 'hero'}
      style={{ ['--ar' as string]: String(ar) } as CSSProperties}
      onClick={onOpen}
      aria-label={item.alt ? `Open image: ${item.alt}` : 'Open image'}
      data-testid="post-media-image"
    >
      <Clip $fit={fit} $pos={objectPosition}>
        <img
          {...imgProps}
          onLoad={(e) => {
            if (measured !== null) return;
            const el = e.currentTarget;
            if (!el.naturalWidth || !el.naturalHeight) return;
            const r = el.naturalWidth / el.naturalHeight;
            rememberRatio(item.url, r);
            setMeasured(r);
          }}
        />
      </Clip>
    </Frame>
  );
}
